import { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, CircularProgress, Grid, Chip, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab,
  TextField, MenuItem, Paper, useMediaQuery, useTheme,
} from '@mui/material';
import { TrendingUp, FilterList } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ParentAssessments() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterYear, setFilterYear] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const lRes = await axios.get(`${API}/api/guardians/me/learners`, { headers: hdrs() });
        const kids = lRes.data?.data || [];
        setLearners(kids);
        if (kids.length > 0) {
          const l = kids[0];
          setSelectedLearner(l);
          loadAssessments(l.admNo);
        }
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.email]);

  const loadAssessments = async (admNo) => {
    try {
      const res = await axios.get(`${API}/api/academics/learner/${admNo}/results`, { headers: hdrs() });
      setAssessments(res.data || []);
    } catch (e) {
      console.error('Error loading assessments:', e);
      setAssessments([]);
    }
  };

  // Get unique years and terms from assessments
  const years = useMemo(() => {
    const yrs = new Set();
    assessments.forEach(a => {
      if (a.year) yrs.add(a.year);
      if (a.academicYear) yrs.add(a.academicYear);
    });
    return Array.from(yrs).sort().reverse();
  }, [assessments]);

  const terms = useMemo(() => {
    const trms = new Set();
    const filtered = assessments.filter(a => !filterYear || a.year === filterYear || a.academicYear === filterYear);
    filtered.forEach(a => {
      if (a.term) trms.add(a.term);
    });
    return Array.from(trms).sort();
  }, [assessments, filterYear]);

  const subjects = useMemo(() => {
    const sbjs = new Set();
    const filtered = assessments.filter(a => {
      const matches = (!filterYear || a.year === filterYear || a.academicYear === filterYear);
      return matches;
    });
    filtered.forEach(a => {
      if (a.subject) sbjs.add(a.subject);
    });
    return Array.from(sbjs).sort();
  }, [assessments, filterYear]);

  // Filter assessments
  const filteredAssessments = useMemo(() => {
    return assessments.filter(a => {
      const yearMatch = !filterYear || a.year === filterYear || a.academicYear === filterYear;
      const termMatch = !filterTerm || a.term === filterTerm;
      const subjectMatch = !filterSubject || a.subject === filterSubject;
      return yearMatch && termMatch && subjectMatch;
    });
  }, [assessments, filterYear, filterTerm, filterSubject]);

  const termResults = filteredAssessments.filter(a => a.type === 'term') || [];
  const unitResults = filteredAssessments.filter(a => a.type === 'unit') || [];
  const exams = filteredAssessments.filter(a => a.type === 'exam') || [];

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  // Mobile Card View for results
  const ResultCard = ({ result, type }) => (
    <Card sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">Subject</Typography>
          <Typography variant="body2" fontWeight={600}>{result.subject}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">
            {type === 'term' ? 'Term' : type === 'unit' ? 'Unit' : 'Score'}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {result.term || result.unit || `${result.score}%`}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">
            {type === 'unit' ? 'Score / Out Of' : 'Score'}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary">
            {type === 'unit' ? `${result.score}/${result.outOf}` : `${result.score}%`}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
          <Chip
            label={
              type === 'unit'
                ? (result.passed ? 'Passed' : 'Failed')
                : result.grade
            }
            size="small"
            color={
              type === 'unit'
                ? (result.passed ? 'success' : 'error')
                : (result.score >= 70 ? 'success' : result.score >= 50 ? 'warning' : 'error')
            }
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Card>
  );

  return (
    <Container maxWidth="md">
      <Box py={3}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <TrendingUp sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
            Assessment Results
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          View your child's test results and academic assessments (readonly). Results are finalized by the school.
        </Alert>

        {/* Learner Selector */}
        {learners.length > 1 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {learners.map(l => (
              <Chip
                key={l._id}
                label={l.name}
                onClick={() => {
                  setSelectedLearner(l);
                  loadAssessments(l.admNo);
                  setFilterYear('');
                  setFilterTerm('');
                  setFilterSubject('');
                }}
                color={selectedLearner?._id === l._id ? 'primary' : 'default'}
                variant={selectedLearner?._id === l._id ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterList sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>Filters</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth size="small"
                label="Year"
                value={filterYear}
                onChange={e => setFilterYear(e.target.value)}
              >
                <MenuItem value="">All Years</MenuItem>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth size="small"
                label="Term"
                value={filterTerm}
                onChange={e => setFilterTerm(e.target.value)}
              >
                <MenuItem value="">All Terms</MenuItem>
                {terms.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth size="small"
                label="Subject"
                value={filterSubject}
                onChange={e => setFilterSubject(e.target.value)}
              >
                <MenuItem value="">All Subjects</MenuItem>
                {subjects.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Box sx={{ mb: 3, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? 'scrollable' : 'standard'}>
            <Tab label={`Terminal Results (${termResults.length})`} />
            <Tab label={`Unit Tests (${unitResults.length})`} />
            <Tab label={`Exams (${exams.length})`} />
          </Tabs>
        </Box>

        {/* Results - Mobile Card View / Desktop Table View */}
        {tab === 0 && (
          <>
            {termResults.length === 0 ? (
              <Alert severity="info">No terminal results match your filters.</Alert>
            ) : isMobile ? (
              <Box>
                {termResults.map((res, i) => (
                  <ResultCard key={i} result={res} type="term" />
                ))}
              </Box>
            ) : (
              <Card sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Term</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Score</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Grade</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Class Avg</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {termResults.map((res, i) => (
                        <TableRow key={i} hover>
                          <TableCell fontWeight={600}>{res.term}</TableCell>
                          <TableCell>{res.subject}</TableCell>
                          <TableCell align="center" fontWeight={600}>{res.score}%</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={res.grade}
                              size="small"
                              color={res.score >= 70 ? 'success' : res.score >= 50 ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">{res.classAverage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            {unitResults.length === 0 ? (
              <Alert severity="info">No unit test results match your filters.</Alert>
            ) : isMobile ? (
              <Box>
                {unitResults.map((res, i) => (
                  <ResultCard key={i} result={res} type="unit" />
                ))}
              </Box>
            ) : (
              <Card sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Score</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Out of</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unitResults.map((res, i) => (
                        <TableRow key={i} hover>
                          <TableCell fontWeight={600}>{res.unit}</TableCell>
                          <TableCell>{res.subject}</TableCell>
                          <TableCell align="center" fontWeight={600}>{res.score}</TableCell>
                          <TableCell align="center">{res.outOf}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={res.passed ? 'Passed' : 'Failed'}
                              size="small"
                              color={res.passed ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            )}
          </>
        )}

        {tab === 2 && (
          <>
            {exams.length === 0 ? (
              <Alert severity="info">No exam results match your filters.</Alert>
            ) : isMobile ? (
              <Box>
                {exams.map((res, i) => (
                  <ResultCard key={i} result={res} type="exam" />
                ))}
              </Box>
            ) : (
              <Card sx={{ boxShadow: 1, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Exam</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Score</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Grade</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exams.map((res, i) => (
                        <TableRow key={i} hover>
                          <TableCell fontWeight={600}>{res.examName}</TableCell>
                          <TableCell>{res.subject}</TableCell>
                          <TableCell align="center" fontWeight={600}>{res.score}%</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={res.grade}
                              size="small"
                              color={res.score >= 70 ? 'success' : res.score >= 50 ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">{res.date ? new Date(res.date).toLocaleDateString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}
